import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Flex, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Popup from '@databases/atlaskit/Popup';
import { SelectColorPalette } from '@databases/color-picker';
import { messages as intlMessages } from '@databases/intl/messages';
import { isModifierKeyPressed } from '@databases/react';
import { getTextDelta } from '@databases/react/useTextDelta';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import { type SelectOption } from '@databases/types/database';
import { type SelectOptionsChange } from '@databases/utils/database/changesets';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../messages.ts';

const TAG_BUTTON_HEIGHT = '32px';

const rowStyles = xcss({
  flexGrow: 1,
  minWidth: '0px'
});

const triggerStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.neutral.subtle',
  borderRadius: 'radius.small',
  display: 'flex',
  height: TAG_BUTTON_HEIGHT,
  paddingBlock: 'space.075',
  paddingInline: 'space.050',
  width: '100%'
});

const popupStyles = xcss({
  minHeight: '120px',
  padding: 'space.150'
});

const popupWithDuplicateErrorStyles = xcss({
  minHeight: '156px'
});

const tagNameInputStyles = {
  height: '32px',
  paddingBottom: token('space.075'),
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
  paddingTop: token('space.075')
};

const POPUP_WIDTH = 176;

const FOCUS_CLAIM_ATTEMPTS = 3;

export interface SelectOptionEditProps {
  autoFocus: boolean;
  index: number;
  isDisabled: boolean;
  isDuplicateLabel: (label: string) => boolean;
  onChange: (value: SelectOption, change: SelectOptionsChange) => void;
  value: SelectOption;
}

const SelectOptionEdit: FunctionComponent<SelectOptionEditProps> = ({
  autoFocus,
  index,
  isDisabled,
  isDuplicateLabel,
  onChange,
  value
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(autoFocus);
  const [hasDuplicateError, setHasDuplicateError] = useState(false);
  const [inputValue, setInputValue] = useState(value.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(autoFocus);
  const popupRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  const placeholder = useMemo(
    () =>
      formatMessage(intlMessages['select-field-settings.placeholder-tag-label'], {
        number: index + 1
      }),
    [formatMessage, index]
  );

  useEffect(() => {
    if (isOpen) {
      setInputValue(value.label);
    }
  }, [isOpen, value.label]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let attemptsLeft = FOCUS_CLAIM_ATTEMPTS;
    let frame = 0;

    const claimFocus = () => {
      if (document.activeElement !== inputRef.current) {
        inputRef.current?.focus();
      }

      attemptsLeft -= 1;
      if (attemptsLeft > 0) {
        frame = requestAnimationFrame(claimFocus);
      }
    };

    frame = requestAnimationFrame(claimFocus);

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const commitLabel = useCallback(
    (label: string): boolean => {
      const trimmed = label.trim() === '' ? placeholder : label.trim();
      if (trimmed === value.label) {
        return true;
      }

      if (isDuplicateLabel(trimmed)) {
        setHasDuplicateError(true);
        return false;
      }

      onChange(
        { ...value, id: value.id ?? value.label, label: trimmed },
        { type: 'update_label', index, delta: getTextDelta(value.label, trimmed) }
      );

      return true;
    },
    [index, isDuplicateLabel, onChange, placeholder, value]
  );

  const handleColorChange = useCallback(
    (color: SelectOption['color']) => {
      onChange({ ...value, color }, { type: 'set_color', index, color });
    },
    [index, onChange, value]
  );

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setHasDuplicateError(false);
    setInputValue(event.target.value);
  }, []);

  const closePopup = useCallback(
    (label = inputValue) => {
      if (!isOpenRef.current || !commitLabel(label)) {
        return;
      }

      isOpenRef.current = false;
      setIsOpen(false);
    },
    [commitLabel, inputValue]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (rowRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return;
      }

      closePopup();
    };

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closePopup();
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    globalThis.addEventListener('keydown', handleEscapeKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      globalThis.removeEventListener('keydown', handleEscapeKeyDown, true);
    };
  }, [closePopup, isOpen]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isModifierKeyPressed(event) && event.key === 'Enter') {
        closePopup(event.currentTarget.value);
      }
    },
    [closePopup]
  );

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleTriggerClick = useCallback(() => {
    if (isOpenRef.current) {
      closePopup();
      return;
    }

    isOpenRef.current = true;
    setIsOpen(true);
  }, [closePopup]);

  const nameLabel = formatMessage(messages.tagNameInputLabel);
  const tagLabel = value.label === '' ? placeholder : value.label;

  return (
    <Box
      ref={rowRef}
      data-contextual-menu-active-escape-boundary={isOpen ? 'true' : undefined}
      data-contextual-menu-escape-boundary={isOpen ? 'true' : undefined}
      xcss={rowStyles}
    >
      <Popup
        autoFocus={false}
        content={() => (
          <Box
            ref={popupRef}
            data-contextual-menu-escape-boundary="true"
            xcss={[popupStyles, hasDuplicateError && popupWithDuplicateErrorStyles]}
          >
            <Stack space={hasDuplicateError ? 'space.050' : 'space.100'}>
              <Stack space="space.075">
                <Textfield
                  aria-label={nameLabel}
                  isCompact
                  ref={inputRef}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  style={tagNameInputStyles}
                  value={inputValue}
                />
                {hasDuplicateError && (
                  <Flex alignItems="start" gap="space.050">
                    <StatusErrorIcon
                      color={token('color.icon.danger')}
                      label=""
                      size="small"
                    />
                    <Text aria-live="polite" color="color.text.danger" size="small">
                      {formatMessage(messages.tagDuplicateError)}
                    </Text>
                  </Flex>
                )}
              </Stack>
              <SelectColorPalette
                hasPadding={false}
                onChange={handleColorChange}
                value={value.color}
              />
            </Stack>
          </Box>
        )}
        contentContainer={null}
        isOpen={isOpen}
        onClose={handleClose}
        placement="bottom-start"
        shouldRenderToParent
        trigger={({ ref }) => (
          <Pressable
            ref={ref as React.Ref<HTMLButtonElement>}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={formatMessage(messages.tagEditTriggerLabel, { label: tagLabel })}
            isDisabled={isDisabled}
            onClick={handleTriggerClick}
            xcss={triggerStyles}
          >
            <SelectOptionTag option={{ color: value.color, label: tagLabel }} noMargin />
          </Pressable>
        )}
        width={POPUP_WIDTH}
      />
    </Box>
  );
};

export default SelectOptionEdit;
